import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ContentBox from '../components/ContentBox';
import HabboImage from '../components/HabboImage';

const LOOKS_PER_PAGE = 21;

function LookModal({ looks, initialIndex, onClose }) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const look = looks[currentIndex];

    const goPrev = useCallback(() => {
        setCurrentIndex(i => Math.max(0, i - 1));
    }, []);

    const goNext = useCallback(() => {
        setCurrentIndex(i => Math.min(looks.length - 1, i + 1));
    }, [looks.length]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [goPrev, goNext, onClose]);

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={onClose}
        >
            <div
                className="bg-offwhite border border-black/20 rounded shadow-xl flex flex-col items-center gap-4 p-6 min-w-[240px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-sm uppercase tracking-wide font-['Jersey_10'] text-gray-600">
                    Look {currentIndex + 1} of {looks.length}
                </div>

                <div className="flex items-center gap-6">
                    <button
                        onClick={goPrev}
                        disabled={currentIndex === 0}
                        className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed font-bold text-lg"
                    >
                        ←
                    </button>

                    <div className="flex flex-col items-center gap-2">
                        <HabboImage
                            figure={look.figureString}
                            direction={4}
                            head_direction={4}
                            size="b"
                        />
                        <span className="text-sm text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                            {formatDateTime(look.added)}
                        </span>
                    </div>

                    <button
                        onClick={goNext}
                        disabled={currentIndex === looks.length - 1}
                        className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed font-bold text-lg"
                    >
                        →
                    </button>
                </div>

                <button
                    onClick={onClose}
                    className="text-sm uppercase tracking-wide font-['Jersey_10'] text-gray-500 hover:text-gray-800 transition-colors"
                >
                    Close
                </button>
            </div>
        </div>
    );
}

function Player() {
    const { hotel, habboName } = useParams();
    const [playerData, setPlayerData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [looksPage, setLooksPage] = useState(1);
    const [modalIndex, setModalIndex] = useState(null);

    useEffect(() => {
        const fetchPlayerData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}habbo/profile`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        HabboName: habboName,
                        Hotel: hotel
                    })
                });

                const data = await response.json();

                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Could not grab Habbo');
                }

                setPlayerData(data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlayerData();
    }, [hotel, habboName]);

    const calculateHabboAge = (memberSince) => {
        if (!memberSince) return 'Fuck knows';
        const memberDate = new Date(memberSince);
        const today = new Date();
        const diffTime = Math.abs(today - memberDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffYears = Math.floor(diffDays / 365);
        const remainingDays = diffDays % 365;
        if (diffYears > 0) {
            return `${diffYears} year${diffYears > 1 ? 's' : ''}, ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
        }
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Fuck knows';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6 flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/img/frankly-ducks.gif`} alt="Loading..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6">
                <ContentBox theme="default" tint="red" header="Error">
                    <p>Habbo Not Found (This will eventually trigger them being added to dataset</p>
                </ContentBox>
            </div>
        );
    }

    if (!playerData) {
        return (
            <div className="w-full min-h-screen p-4 lg:p-6">
                <ContentBox theme="default" tint="red" header="Not Found">
                    <p>Habbo Not Found (This will eventually trigger them being added to dataset</p>
                </ContentBox>
            </div>
        );
    }

    const allLooks = playerData.looks || [];
    const totalLooksPages = Math.ceil(allLooks.length / LOOKS_PER_PAGE);
    const pagedLooks = allLooks.slice(
        (looksPage - 1) * LOOKS_PER_PAGE,
        looksPage * LOOKS_PER_PAGE
    );

    const handleLookClick = (pageIndex) => {
        const globalIndex = (looksPage - 1) * LOOKS_PER_PAGE + pageIndex;
        setModalIndex(globalIndex);
    };

    return (
        <div className="w-full min-h-screen p-4 lg:p-6">

            {modalIndex !== null && (
                <LookModal
                    looks={allLooks}
                    initialIndex={modalIndex}
                    onClose={() => setModalIndex(null)}
                />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">

                <div className="lg:col-span-4 min-w-0">
                    <ContentBox
                        theme="default"
                        header={`${playerData.habbo.name}'s Profile`}
                        icon={playerData.liveData ? `/img/${playerData.liveData.online ? 'online' : 'offline'}.gif` : null}
                    >
                        <div className="flex flex-col md:flex-row gap-1">
                            <div className="flex-1 flex flex-col gap-1">
                                <div className="bg-gray-200 rounded px-3 py-1">
                                    <span className="font-bold text-sm">Hotel:</span>
                                    <span className="ml-2 text-sm uppercase">{playerData.habbo.hotel}</span>
                                </div>

                                <div className="bg-gray-200 rounded px-3 py-2">
                                    <span className="font-bold text-sm">Member Since:</span>
                                    <span className="ml-2 text-sm">{formatDateTime(playerData.habbo.memberSince)}</span>
                                </div>

                                <div className="bg-gray-200 rounded px-3 py-2">
                                    <span className="font-bold text-sm">Habbo Age:</span>
                                    <span className="ml-2 text-sm">{calculateHabboAge(playerData.habbo.memberSince)}</span>
                                </div>

                                {playerData.liveData && (
                                    <>
                                        <div className="bg-gray-200 rounded px-3 py-2">
                                            <span className="font-bold text-sm">Last Access:</span>
                                            <span className="ml-2 text-sm">{formatDateTime(playerData.liveData.lastAccessTime)}</span>
                                        </div>

                                        {playerData.liveData.motto && (
                                            <div className="bg-gray-200 rounded px-3 py-2">
                                                <span className="font-bold text-sm">Motto:</span>
                                                <span className="ml-2 text-sm">"{playerData.liveData.motto}"</span>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="bg-gray-200 rounded px-3 py-2">
                                    <span className="font-bold text-sm">Indexed Since:</span>
                                    <span className="ml-2 text-sm">{formatDateTime(playerData.habbo.addedTimestamp)}</span>
                                </div>
                            </div>

                            <div className="flex-shrink-0 flex flex-col gap-1">
                                <div className="bg-gray-200 rounded p-3 flex flex-col items-center justify-center gap-2">
                                    <span className="text-[14px] font-['Jersey_10'] mb-0.5 uppercase">Current Look</span>
                                    <HabboImage
                                        habbo={playerData.habbo.name}
                                        hotel={playerData.habbo.hotel}
                                        direction={4}
                                        head_direction={4}
                                        size="b"
                                        crr={9}
                                    />
                                </div>

                                {playerData.liveData && playerData.liveData.selectedBadges && playerData.liveData.selectedBadges.length > 0 && (
                                    <div className="bg-gray-200 rounded p-3 flex flex-col items-center gap-2">
                                        <span className="text-[14px] font-['Jersey_10'] uppercase">Active Badge</span>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {playerData.liveData.selectedBadges.map((badge, index) => (
                                                <div key={index} className="flex flex-col items-center gap-1">
                                                    <img
                                                        src={`${process.env.PUBLIC_URL}/img/badges/${badge.code}.png`}
                                                        alt={badge.name || badge.code}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </ContentBox>
                </div>

                <div className="lg:col-span-8 min-w-0">

                    <ContentBox theme="default" tint="lightskyblue" header="Badges">
                        {playerData.badges && playerData.badges.length > 0 ? (
                            <>
                                <p className="mb-3 text-sm">This is a list of badges we've indexed for this Habbo. This list may not be complete and they may have badges here that they no longer hold in game (example: Hobba or HC badges).</p>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                    {playerData.badges.map((badge, index) => (
                                        <div key={index} className="bg-gray-200 rounded p-2 flex flex-col items-center gap-1">
                                            <span className="text-[14px] text-center font-['Jersey_10'] uppercase">
                                                {badge.badgeName || badge.badgeCode}
                                            </span>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/img/badges/${badge.badgeCode}.png`}
                                                alt={badge.badgeName || badge.badgeCode}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p>No badges found</p>
                        )}
                    </ContentBox>

                    <ContentBox theme="default" tint="gold" header="Previous Looks">
                        {allLooks.length > 0 ? (
                            <>
                                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-3">
                                    {pagedLooks.map((look, index) => (
                                        <div
                                            key={`${looksPage}-${index}`}
                                            className="bg-gray-200 rounded p-2 flex flex-col items-center gap-1 cursor-pointer hover:bg-gray-300 transition-colors"
                                            onClick={() => handleLookClick(index)}
                                        >
                                            <HabboImage
                                                figure={look.figureString}
                                                direction={4}
                                                head_direction={4}
                                                size="a"
                                            />
                                            <span className="text-[14px] text-center font-['Jersey_10']">
                                                {formatShortDate(look.added)}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {totalLooksPages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-300">
                                        <button
                                            onClick={() => setLooksPage(p => Math.max(1, p - 1))}
                                            disabled={looksPage === 1}
                                            className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-1.5 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded"
                                        >
                                            ← Prev
                                        </button>

                                        <span className="text-xs text-gray-600 font-['Jersey_10'] uppercase tracking-wide">
                                            Page {looksPage} of {totalLooksPages} &mdash; {allLooks.length} looks total
                                        </span>

                                        <button
                                            onClick={() => setLooksPage(p => Math.min(totalLooksPages, p + 1))}
                                            disabled={looksPage === totalLooksPages}
                                            className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-1.5 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p>No previous looks found</p>
                        )}
                    </ContentBox>
                </div>

            </div>
        </div>
    );
}

export default Player;